/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useEffect, useState } from 'react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  createHttpLink,
} from '@apollo/client';
import { concat } from '@apollo/client/link/core';
import { v4 as uuidv4 } from 'uuid';
import type { DealerContextServiceV1, UnsubscribeFn } from '@oneaudi/dealer-context-service';
import { I18nextProvider } from 'react-i18next';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import VehicleProvider from './components/VehicleProvider';
import i18n from '../i18n';
import GlobalStyles from './styles';
import VehicleHistory from './components/VehicleHistory';
import ErrorBoundary from './components/ErrorBoundary';
import { IS_TIER3_PAGE } from './constants';
import { getOneGraphUrl } from './utils/getGraphqlUrl';
import { SafeThemeProvider } from './components/SafeThemeProvider';

interface FeatureAppProps {
  dealerContextService?: DealerContextServiceV1;
  trackingService?: TrackingServiceV2;
  configProvider?: GfaServiceConfigProviderV1;
}

const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-correlation-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const httpLink = createHttpLink({
  uri: getOneGraphUrl(),
});

const apolloClient = new ApolloClient({
  link: concat(correlationIdLink, httpLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

const FeatureApp: React.FC<FeatureAppProps> = ({
  dealerContextService,
  trackingService,
}: FeatureAppProps) => {
  const [oaddDealerId, setOaddDealerId] = useState('');

  React.useEffect(() => {
    if (!trackingService) return;

    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, [trackingService]);

  useEffect(() => {
    const unsubscribeDealerContext: UnsubscribeFn | undefined = dealerContextService?.subscribe(
      (dealerContext) => {
        if (dealerContext) {
          const dealerId = dealerContext.id;
          console.log(
            '🚀 Dealer Context in fa-vdp-history-section ~ useEffect ~ dealerId:',
            dealerId,
          );
          if (dealerId.length) setOaddDealerId(dealerId);
        }
      },
    );
    return (): void => {
      if (dealerContextService) {
        if (unsubscribeDealerContext) {
          unsubscribeDealerContext();
        }
      }
    };
  }, [dealerContextService]);

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <SafeThemeProvider>
          <ApolloProvider client={apolloClient}>
            <VehicleProvider oaddDealerId={oaddDealerId} isTier3={IS_TIER3_PAGE}>
              <div className="resetWrapper">
                <VehicleHistory />
              </div>
            </VehicleProvider>
          </ApolloProvider>
          <GlobalStyles />
        </SafeThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
